import { __rest } from "tslib";
import { URIFormat } from './enums/uri_format';
import { URITypeMap } from './enums/uri_type';
import { OPEN_HTTPS_PREFIX } from './enums/prefix';
import { URIParticle } from './enums/uri_particle';
import { parse } from './_internal/parser';
import { getComponents } from './_internal/utils';
import { idToHex } from './utils';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _extend = typeof Object.assign === 'function'
    ? (t, e) => {
        Object.assign(t, e);
    }
    : (t, e) => {
        for (const k in e) {
            if (!Object.prototype.hasOwnProperty.call(e, k)) {
                continue;
            }
            t[k] = e[k];
        }
    };
class URIImpl {
    /**
     * Creates a new URI object of a specified type and with props.
     *
     * @param type - The type of this URI
     * @param props - An object containing the properties from this object
     */
    constructor(type, props = {}) {
        this.type = type;
        this.hasBase62Id = !!props.hasBase62Id;
        _extend(this, props);
    }
    toURI() {
        const components = getComponents(this, URIFormat.URI);
        return `spotify:${components.join(':')}`;
    }
    toString() {
        return this.toURI();
    }
    toURLPath(leadingSlash = false) {
        let components = getComponents(this, URIFormat.URL);
        if (components[0] === URIParticle.APP) {
            components.shift();
        }
        // Some URIs are allowed to have empty components. It should be investigated
        // whether we need to strip empty components at all from any URIs. For now,
        // we check specifically for tracksets and local tracks and strip empty
        // components for all other URIs.
        //
        // For tracksets, it's permissible to have a path that looks like
        // 'trackset//trackURI' because the identifier parameter for a trackset can
        // be blank. For local tracks, some metadata can be missing, like missing
        // album name would be 'spotify:local:artist::track:duration'.
        const isTrackset = components[0] === URITypeMap.TRACKSET;
        const isLocalTrack = components[0] === URITypeMap.LOCAL_TRACK;
        const shouldStripEmptyComponents = !isTrackset && !isLocalTrack;
        if (shouldStripEmptyComponents) {
            const _temp = [];
            for (let i = 0, l = components.length; i < l; i++) {
                const component = components[i];
                if (!!component) {
                    _temp.push(component);
                }
            }
            components = _temp;
        }
        const path = components.join('/');
        return leadingSlash ? `/${path}` : path;
    }
    getPath() {
        return this.toString().replace(/[#?].*/, '');
    }
    toURL(prefix) {
        return `${prefix || OPEN_HTTPS_PREFIX}${this.toURLPath()}`;
    }
    clone() {
        return new URIImpl(this.type, this);
    }
}
export function createURI(type, props) {
    return new URIImpl(type, props);
}
/**
 * Creates a new URI object from a parsed string argument.
 *
 * @param uri - The string that will be parsed into a URI object.
 * @param options - An options object that allows the modification of how we parse a URI.
 * @returns The parsed URI object.
 * @throws TypeError If the string argument is not a valid URI, a TypeError will
 *   be thrown.
 */
export function parseURIFromString(uri, options) {
    if (typeof uri !== 'string') {
        throw new TypeError('Argument `uri` must be a string.');
    }
    return parse(uri, options === null || options === void 0 ? void 0 : options.parseUnknown);
}
export function parseURI(maybeURI, maybeOptionsOrAllowedTypes) {
    var _a, _b, _c;
    if (!maybeURI) {
        return null;
    }
    let calculatedOptions = {};
    if (maybeOptionsOrAllowedTypes) {
        if (Array.isArray(maybeOptionsOrAllowedTypes)) {
            // While we support the signature where the second arg could be allowedTypes we need to do this runtime check.
            // If the second arg is an array then we assume this is allowedTypes.
            calculatedOptions.allowedTypes = maybeOptionsOrAllowedTypes;
        }
        else {
            calculatedOptions = maybeOptionsOrAllowedTypes;
        }
    }
    if (maybeURI instanceof URIImpl) {
        if (((_a = calculatedOptions.allowedTypes) === null || _a === void 0 ? void 0 : _a.indexOf(maybeURI.type)) === -1) {
            return null;
        }
        return maybeURI.clone();
    }
    if (typeof maybeURI === 'string') {
        try {
            const parsed = parseURIFromString(maybeURI, calculatedOptions);
            if (((_b = calculatedOptions.allowedTypes) === null || _b === void 0 ? void 0 : _b.indexOf(parsed.type)) === -1) {
                return null;
            }
            return parsed;
        }
        catch (_d) {
            return null;
        }
    }
    if (maybeURI === null || maybeURI === void 0 ? void 0 : maybeURI.type) {
        if (((_c = calculatedOptions.allowedTypes) === null || _c === void 0 ? void 0 : _c.indexOf(maybeURI.type)) === -1) {
            return null;
        }
        const { type } = maybeURI, props = __rest(maybeURI, ["type"]);
        return new URIImpl(type, props);
    }
    return null;
}
/**
 * Returns the HEX encoded version of the Base62 ID
 *
 * @param uri - The target URI.
 * @returns The ID hex-encoded
 */
export function getURIHexId(uri) {
    return uri.hasBase62Id && uri.id ? idToHex(uri.id) : null;
}
//# sourceMappingURL=uri.js.map